import React, { useState } from "react";

export default function SyntraMfgContractManufacturingForm() {
  const [factoryName, setFactoryName] = useState("");
  const [factoryType, setFactoryType] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [city, setCity] = useState("");
  const [stateDistrict, setStateDistrict] = useState("");
  const [productType, setProductType] = useState("");
  const [productName, setProductName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [productionDate, setProductionDate] = useState("");
  const [certifications, setCertifications] = useState([]);
  const [price, setPrice] = useState("");
  const [deliveryType, setDeliveryType] = useState("");
  const [packaging, setPackaging] = useState("");
  const [minOrder, setMinOrder] = useState("");
  const [longTerm, setLongTerm] = useState("");
  const [notes, setNotes] = useState("");
  const [showAbout, setShowAbout] = useState(false);

  const factoryTypes = ["Manufacturer", "OEM", "Contract Manufacturer", "Supplier"];
  const productTypes = ["Components", "Spare Parts", "Finished Goods", "Assemblies", "Others"];
  const deliveryOptions = ["Factory Pickup", "Buyer Pickup", "Courier / Logistics", "Custom Shipping"];
  const certOptions = ["ISO 9001", "CE", "BIS", "Other"];

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!productType || !productName || !quantity || !factoryName || !pinCode) {
      alert("Please fill all required fields (Factory Name, Product Type, Product Name, Quantity, PIN).");
      return;
    }

    alert(`Manufacturing Contract Lead Submitted:
    Factory Name: ${factoryName}
    Factory Type: ${factoryType}
    PIN: ${pinCode}
    City: ${city}
    State/District: ${stateDistrict}
    Product Type: ${productType}
    Product Name: ${productName}
    Quantity: ${quantity}
    Expected Production Date: ${productionDate}
    Certifications: ${certifications.join(", ")}
    Price Expectation: ${price}
    Delivery Type: ${deliveryType}
    Packaging: ${packaging}
    Minimum Order: ${minOrder}
    Long Term Contract: ${longTerm}
    Notes: ${notes}`);
  };

  const handleCertificationChange = (cert) => {
    setCertifications((prev) =>
      prev.includes(cert) ? prev.filter((c) => c !== cert) : [...prev, cert]
    );
  };

  return (
    <div className="p-4 bg-black shadow-lg rounded-md relative">
      <h2 className="text-xl lg:text-xl font-bold mb-4 text-yellow-200 text-center">Contract Manufacturing Lead Form</h2>
      <button
        type="button"
        onClick={() => setShowAbout(true)}
        className="w-64 mb-4 bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
      >
        About Contract Manufacturing
      </button>
      {showAbout && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-blue-600 p-6 rounded-2xl max-w-2xl shadow-xl overflow-y-auto max-h-[80vh]">
            <h3 className="text-2xl font-bold mb-4 text-center text-yellow-100">
              About Contract Manufacturing
            </h3>
            <div className="space-y-4 text-white text-sm leading-relaxed">
              <p>
                Contract manufacturing allows factories, OEMs, and suppliers to offer products under agreed terms.
                It ensures market access, transparent pricing, and predictable demand for both parties.
              </p>
              <p>
                <b>SyntraMfg Contract Manufacturing Module</b> helps manufacturers submit leads, negotiate terms,
                and get connected with verified buyers efficiently.
              </p>
            </div>
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAbout(false)}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4 text-white">
        <div>
          <label className="block font-medium mb-1">Factory / Supplier Name *</label>
          <input
            type="text"
            value={factoryName}
            onChange={(e) => setFactoryName(e.target.value)}
            placeholder="Enter factory or supplier name"
            className="h-10 w-full border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Type</label>
          <select
            value={factoryType}
            onChange={(e) => setFactoryType(e.target.value)}
            className="h-10 w-40 border p-2 rounded text-black"
          >
            <option value="">-- Choose Type --</option>
            {factoryTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-medium mb-1">PIN Code *</label>
          <input
            type="text"
            value={pinCode}
            onChange={(e) => setPinCode(e.target.value)}
            placeholder="Enter PIN Code"
            className="w-32 border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">City</label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Enter city"
            className="w-40 border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">State / District</label>
          <input
            type="text"
            value={stateDistrict}
            onChange={(e) => setStateDistrict(e.target.value)}
            placeholder="Enter state or district"
            className="w-64 border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Product Type *</label>
          <select
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            className="h-10 w-80 border p-2 rounded text-black"
          >
            <option value="">-- Choose Product Type --</option>
            {productTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-medium mb-1">Product Name / Model *</label>
          <input
            type="text"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="Enter product name or model"
            className="h-10 w-full border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Expected Quantity *</label>
          <input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="Enter expected quantity"
            className="h-10 w-20 border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Expected Production Date</label>
          <input
            type="month"
            value={productionDate}
            onChange={(e) => setProductionDate(e.target.value)}
            className="h-10 w-40 border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Certifications</label>
          <div className="flex flex-wrap gap-2">
            {certOptions.map((cert) => (
              <label key={cert} className="bg-cyan-300 px-2 py-1 rounded text-black cursor-pointer">
                <input
                  type="checkbox"
                  checked={certifications.includes(cert)}
                  onChange={() => handleCertificationChange(cert)}
                  className="mr-1"
                />
                {cert}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block font-medium mb-1">Price Expectation (optional)</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="Price per unit (optional)"
            className="h-10 w-40 border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Preferred Delivery Type</label>
          <select
            value={deliveryType}
            onChange={(e) => setDeliveryType(e.target.value)}
            className="h-10 w-64 border p-2 rounded text-black"
          >
            <option value="">-- Choose Delivery Type --</option>
            {deliveryOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block font-medium mb-1">Packaging Type</label>
          <input
            type="text"
            value={packaging}
            onChange={(e) => setPackaging(e.target.value)}
            placeholder="Bulk / Retail / Custom"
            className="h-10 w-40 border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Minimum Order Quantity</label>
          <input
            type="number"
            value={minOrder}
            onChange={(e) => setMinOrder(e.target.value)}
            placeholder="Enter minimum order quantity"
            className="h-10 w-32 border p-2 rounded text-black"
          />
        </div>
        <div>
          <label className="block font-medium mb-1">Interested in Long-Term Contract</label>
          <select
            value={longTerm}
            onChange={(e) => setLongTerm(e.target.value)}
            className="h-10 w-40 border p-2 rounded text-black"
          >
            <option value="">-- Select --</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div>
          <label className="block font-medium mb-1">Notes / Special Requirements</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter any additional information"
            style={{ height: "300px", width: "100%" }}
            className="w-full border p-2 rounded bg-gray-700 text-white"
          />
        </div>
        <button
          type="submit"
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 w-24"
        >
          Submit
        </button>
      </form>
    </div>
  );
}
